/**
 * `GET /api/v2/manifest` — the newest complete published map bundle
 * (`metro-server/docs/api-v2.md`): `{id, format, city, sequence, version,
 * files[]}` inside the `{data, success}` envelope, with an ETag.
 */

export interface ManifestFile {
  readonly name: string
  readonly role: string
  readonly theme?: string
  /** sha256 of the uncompressed bytes — the file's address. */
  readonly sha256: string
  readonly size: number
  /** sha256 of the gzip body `/api/v2/files/{sha256}` serves. */
  readonly gzipSha256: string
  readonly gzipSize: number
}

export interface RemoteManifest {
  /** sha256 of the published `bundle.json` — the bundle's identity. */
  readonly id: string
  readonly format: number
  readonly city: string
  readonly sequence: number
  readonly version: string
  readonly files: readonly ManifestFile[]
}

export type ManifestFetch =
  | { readonly kind: 'unchanged' }
  | { readonly kind: 'manifest'; readonly manifest: RemoteManifest; readonly etag: string | null }
  | { readonly kind: 'failed'; readonly reason: string }

const SHA256 = /^[0-9a-f]{64}$/

function isFile(v: unknown): v is ManifestFile {
  if (typeof v !== 'object' || v === null) return false
  const f = v as Record<string, unknown>
  return (
    typeof f.name === 'string' &&
    f.name !== '' &&
    !f.name.includes('/') &&
    typeof f.role === 'string' &&
    (f.theme === undefined || typeof f.theme === 'string') &&
    typeof f.sha256 === 'string' &&
    SHA256.test(f.sha256) &&
    typeof f.gzipSha256 === 'string' &&
    SHA256.test(f.gzipSha256) &&
    Number.isInteger(f.size) &&
    (f.size as number) >= 0 &&
    Number.isInteger(f.gzipSize) &&
    (f.gzipSize as number) >= 0
  )
}

/** The manifest inside a `/api/v2/manifest` body, or `null` when it is malformed. */
export function parseManifest(body: unknown): RemoteManifest | null {
  if (typeof body !== 'object' || body === null) return null
  const data = (body as { data?: unknown }).data
  if (typeof data !== 'object' || data === null) return null
  const m = data as Record<string, unknown>
  if (
    typeof m.id !== 'string' ||
    !SHA256.test(m.id) ||
    !Number.isInteger(m.format) ||
    typeof m.city !== 'string' ||
    !Number.isInteger(m.sequence) ||
    typeof m.version !== 'string' ||
    !Array.isArray(m.files) ||
    !m.files.every(isFile)
  ) {
    return null
  }
  const names = new Set(m.files.map((f) => f.name))
  if (names.size !== m.files.length) return null
  return {
    id: m.id,
    format: m.format as number,
    city: m.city,
    sequence: m.sequence as number,
    version: m.version,
    files: m.files,
  }
}

/** Conditional GET of the manifest; never throws. */
export async function fetchManifest(
  fetchImpl: typeof fetch,
  url: string,
  etag: string | null,
): Promise<ManifestFetch> {
  let res: Response
  try {
    res = await fetchImpl(url, {
      headers: etag === null ? {} : { 'If-None-Match': etag },
      cache: 'no-store',
    })
  } catch {
    return { kind: 'failed', reason: 'network' }
  }
  if (res.status === 304) return { kind: 'unchanged' }
  if (!res.ok) return { kind: 'failed', reason: `http_${res.status}` }
  let body: unknown
  try {
    body = await res.json()
  } catch {
    return { kind: 'failed', reason: 'manifest_invalid' }
  }
  const manifest = parseManifest(body)
  if (manifest === null) return { kind: 'failed', reason: 'manifest_invalid' }
  return { kind: 'manifest', manifest, etag: res.headers.get('ETag') }
}
